/**
 * Lives that repeat, as the host's screens say them: the words for a session's
 * day and who is told. Designed in `prototype/recurring-lives.html`; the rule
 * itself and its own words are `shared/live-series.ts`, the Repeats picker's are
 * `repeats.ts`.
 */
import { LIVE_LENGTHS } from "../../shared/live-entry";
import type { SeriesEnds, SeriesRule } from "../../shared/live-series";
import { labelParts, wallClock } from "../../shared/live-time";
import type { LiveSessionView } from "../../shared/types";

// ── the words for a day ──────────────────────────────────────────────────
const WALL = (date: string, opts: Intl.DateTimeFormatOptions): string => {
  const [y, m, d] = date.split("-").map(Number);
  return new Intl.DateTimeFormat("en-US", { timeZone: "UTC", ...opts }).format(Date.UTC(y, m - 1, d));
};

/** "Thu, Sep 24" for a wall date. */
export function wallDayShort(date: string): string {
  return WALL(date, { weekday: "short", month: "short", day: "numeric" });
}

/** "Thu, Sep 24" for an instant, on the live's own calendar. */
export function dayShort(iso: string, tz: string): string {
  const p = labelParts(iso, tz, "short");
  return p ? `${p.weekday}, ${p.month} ${p.day}` : "";
}

/** "Thursday, September 24". */
export function dayLong(iso: string, tz: string): string {
  const p = labelParts(iso, tz, "long");
  return p ? `${p.weekday}, ${p.month} ${p.day}` : "";
}

/** "Thursday, Sep 24". */
export function dayMid(iso: string, tz: string): string {
  const long = labelParts(iso, tz, "long");
  const short = labelParts(iso, tz, "short");
  return long && short ? `${long.weekday}, ${short.month} ${short.day}` : "";
}

/** "Sep 24". */
export function monthDay(iso: string, tz: string): string {
  const p = labelParts(iso, tz, "short");
  return p ? `${p.month} ${p.day}` : "";
}

/** "7:00 PM". */
export function timeOf(iso: string, tz: string): string {
  return labelParts(iso, tz, "long")?.time ?? "";
}

/** "1 hour": a length as the live's controls offer it. */
export function lengthWords(min: number): string {
  return LIVE_LENGTHS.find((l) => l.min === min)?.label ?? `${min} min`;
}

/** The wall date of an instant in a zone. */
export function wallDate(iso: string, tz: string): string {
  return wallClock(iso, tz)?.date ?? iso.slice(0, 10);
}

/** " We told the 18 with a seat." after a change, or nothing when nobody has one. */
export function toldLine(seats: number): string {
  if (seats <= 0) return "";
  return seats === 1 ? " We told the one person with a seat." : ` We told the ${seats.toLocaleString("en-US")} with a seat.`;
}

/** "18 people have a seat." Empty when nobody has one. */
export function seatsHead(seats: number): string {
  if (seats <= 0) return "";
  return seats === 1 ? "1 person has a seat." : `${seats.toLocaleString("en-US")} people have a seat.`;
}

/** A series with one session a week: its sessions and its words are about weeks. */
export function weekly(rule: Pick<SeriesRule, "unit" | "weekdays">): boolean {
  return rule.unit === "week" && rule.weekdays.length === 1;
}

// ── the sessions ────────────────────────────────────────────────────────
/** A session whose time has not come: it can still move, be skipped or come back. */
export function aheadOf(s: Pick<LiveSessionView, "state" | "startsAt">, now: number): boolean {
  return s.state === "scheduled" || (s.state === "skipped" && Date.parse(s.startsAt) > now);
}

/**
 * The session Change the series starts at: the first one still to come,
 * skipped or not. A skipped one whose day has gone counts as past.
 */
export function changeFrom(sessions: readonly LiveSessionView[], now: number): LiveSessionView | null {
  return sessions.toSorted((a, b) => a.seq - b.seq).find((s) => aheadOf(s, now)) ?? null;
}

/** Where the series puts a session, as a wall date: its own day, or where it was moved from. */
export function slotOf(s: Pick<LiveSessionView, "startsAt" | "movedFrom" | "timezone">): string {
  return wallDate(s.movedFrom ?? s.startsAt, s.timezone);
}

/** Two ends say the same thing. */
export function sameEnds(a: SeriesEnds, b: SeriesEnds): boolean {
  if (a.kind === "after" && b.kind === "after") return a.count === b.count;
  if (a.kind === "on" && b.kind === "on") return a.date === b.date;
  return a.kind === b.kind;
}
